import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Send } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you! We'll reach out shortly.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="bg-cream py-20 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5 reveal">
          <div className="text-[11px] uppercase tracking-[0.3em] text-gold font-semibold">
            Say hello
          </div>
          <h2 className="font-display text-4xl md:text-5xl text-cocoa mt-3 leading-tight">
            Catering, feedback or just hungry?
          </h2>
          <p className="mt-5 text-cocoa-soft leading-relaxed">
            Leave a note and our kitchen team will get back to you within the
            same day. For large orders (10+ handis), give us a 24-hour heads up.
          </p>

          <div className="mt-10 space-y-5 text-sm">
            <div className="flex gap-3 items-start">
              <Mail className="text-gold mt-0.5" size={18} />
              <div>
                <div className="text-xs uppercase tracking-wider text-cocoa-soft">
                  Email
                </div>
                <a
                  href="mailto:help@andhrabiryani.co"
                  className="text-cocoa font-semibold hover:text-chilli"
                  data-testid="contact-email-link"
                >
                  help@andhrabiryani.co
                </a>
              </div>
            </div>
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          data-testid="contact-form"
          className="lg:col-span-7 bg-card-warm border border-cream-deep rounded-2xl p-8 md:p-10 shadow-sm reveal"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="text-xs uppercase tracking-wider text-cocoa-soft font-semibold">
                Name
              </label>
              <input
                value={form.name}
                onChange={onChange("name")}
                data-testid="contact-input-name"
                type="text"
                placeholder="Your name"
                className="mt-2 w-full rounded-lg border border-cream-deep bg-white px-4 py-3 text-cocoa placeholder:text-[#A88E72] focus:outline-none focus:border-[#C8A14B] focus:ring-2 focus:ring-[#C8A14B]/30 transition"
                required
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-cocoa-soft font-semibold">
                Email
              </label>
              <input
                value={form.email}
                onChange={onChange("email")}
                data-testid="contact-input-email"
                type="email"
                placeholder="you@example.com"
                className="mt-2 w-full rounded-lg border border-cream-deep bg-white px-4 py-3 text-cocoa placeholder:text-[#A88E72] focus:outline-none focus:border-[#C8A14B] focus:ring-2 focus:ring-[#C8A14B]/30 transition"
                required
              />
            </div>
          </div>

          <div className="mt-5">
            <label className="text-xs uppercase tracking-wider text-cocoa-soft font-semibold">
              Message
            </label>
            <textarea
              value={form.message}
              onChange={onChange("message")}
              data-testid="contact-input-message"
              rows={5}
              placeholder="A note about catering, feedback or a bulk order…"
              className="mt-2 w-full rounded-lg border border-cream-deep bg-white px-4 py-3 text-cocoa placeholder:text-[#A88E72] focus:outline-none focus:border-[#C8A14B] focus:ring-2 focus:ring-[#C8A14B]/30 transition resize-none"
              required
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="contact-submit-btn"
            className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-[#C0392B] text-white hover:bg-[#D03A2A] disabled:opacity-60 disabled:cursor-not-allowed hover:-translate-y-0.5 transition-all px-7 py-3.5 font-semibold shadow-md"
          >
            <Send size={16} />
            {loading ? "Sending…" : "Send Message"}
          </button>
          <p className="mt-3 text-xs text-cocoa-soft">
            By submitting, you agree to be contacted by our team via email.
          </p>
        </form>
      </div>
    </section>
  );
}
